// Provider identity enum. Every neutral-layer payment operation
// carries (or yields) a `provider` field so consumers can trace which
// processor actually handled a given request/outcome, even though the
// request/result shape is identical across providers.
//
// Adding a new provider requires one edit here + one provider
// implementation + one compositionRoot registration. No consumer-side
// change. This is the lever the peer-provider architecture turns on.

export const PAYMENT_PROVIDERS = ["trx"] as const;
export type PaymentProviderName = (typeof PAYMENT_PROVIDERS)[number];

/**
 * `true` only for a provider id this platform can route to TODAY.
 *
 * Stored documents can carry a provider id that is no longer supported (a
 * retired processor's `"cardpointe"` is the known case — no merchant was ever
 * boarded on it, but the value is legal history). Such a value must FAIL
 * CLOSED: it is never re-interpreted as another processor, and it must not
 * crash the read that loaded it. Narrow with this guard (or
 * {@link parsePaymentProviderName}) wherever a stored or wire value becomes a
 * `PaymentProviderName`, instead of an `as` cast.
 */
export function isPaymentProviderName(
  value: unknown,
): value is PaymentProviderName {
  return (
    typeof value === "string" &&
    (PAYMENT_PROVIDERS as readonly string[]).includes(value)
  );
}

/**
 * Narrow a stored / wire provider id. Returns `null` for anything that is
 * not a supported provider — an unsupported provider is "not configured",
 * and every caller already fails closed on `null`.
 */
export function parsePaymentProviderName(
  value: unknown,
): PaymentProviderName | null {
  return isPaymentProviderName(value) ? value : null;
}

// ISO 4217 alpha-3 currency codes that our neutral layer accepts. The
// set is intentionally tight — expand when a tenant needs a new
// currency and every provider has been certified for it.
export const PAYMENT_CURRENCIES = ["USD", "CAD"] as const;
export type PaymentCurrency = (typeof PAYMENT_CURRENCIES)[number];

// Rails surfaced to consumer apps. Each provider maps these to its own
// internal `TranType`. Unknown rails are rejected at the neutral layer
// rather than passed through silently.
export const PAYMENT_RAILS = [
  "Card",        // credit / debit brand cards
  "Ach",         // US / EFT Canada
  "Wallet",      // Apple Pay, Google Pay
  "SavedToken",  // a previously-tokenized instrument reused by guid
  "Cash",        // not a processor operation; kept here for domain symmetry
  "GiftCard",    // closed-loop house tender
] as const;
export type PaymentRail = (typeof PAYMENT_RAILS)[number];

// Card capture entry mode for reconciliation / compliance reports.
//
// Faithfulness note: this is the merchant's ATTESTATION of how the card was
// captured, and it drives interchange rate + fraud classification. Keep it
// honest — `Manual` is a keyed card that is PRESENT (broken reader); a keyed
// card-NOT-present telephone order is `MotoPhone` (distinct ECI). Mis-tagging
// either mis-rates the transaction and misstates the entry condition to the
// networks.
export const PAYMENT_ENTRY_MODES = [
  "Manual",      // keyed pan, card PRESENT (e.g. damaged stripe/chip) — card-present rate
  "Swipe",       // mag stripe
  "Chip",        // EMV contact
  "Contactless", // EMV contactless + wallets
  "OnFile",      // stored-credential / tokenized reuse
  "Ecommerce",   // card-not-present web entry (internet) → TRX Eci "7"
  "MotoPhone",   // card-not-present one-time TELEPHONE order (MOTO) → TRX Eci "T" (§22.171)
] as const;
export type PaymentEntryMode = (typeof PAYMENT_ENTRY_MODES)[number];

// The entry modes that are card-not-present BY CONSTRUCTION. This is the
// single source of truth: it drives AVS/CVV fraud rules
// (`isCardNotPresentEntryMode` in `services/payments/fraudEvaluation`) AND the
// TRX `IndustryData.Industry` classifier (`resolveTrxIndustry` in
// `services/trx/adapters/Sale`). Those two were independent hand-written
// lists, and they drifted: the fraud list included `OnFile`, the TRX list did
// not, so every stored-credential charge — a saved-card online order, an
// invoice payment, a dunning retry — went to the gateway tagged as a
// card-PRESENT `Restaurant` transaction and, because `Eci` is CNP-gated, with
// no e-commerce indicator at all. The processor had to force those to settle.
// Derive from this constant; never re-list the modes.
//
// `OnFile` is unconditionally card-not-present: reusing a stored credential
// means the card is by definition not at the terminal. No card-present flow
// uses it — a restaurant tab is Auth+Capture on a swipe, never `OnFile`.
export const CARD_NOT_PRESENT_ENTRY_MODES = [
  "OnFile",
  "Ecommerce",
  "MotoPhone",
] as const satisfies readonly PaymentEntryMode[];
export type CardNotPresentEntryMode =
  (typeof CARD_NOT_PRESENT_ENTRY_MODES)[number];

/**
 * `true` when the entry mode means the card was NOT at the terminal.
 *
 * Accepts `string | undefined` rather than `PaymentEntryMode` because callers
 * on the reporting/fraud side receive provider-echoed values that have not been
 * narrowed to the enum yet. An unrecognized value answers `false` — the
 * card-present (conservative) reading, which never widens CNP treatment on a
 * value we do not understand.
 */
export function isCardNotPresentEntryMode(
  entryMode: string | undefined,
): boolean {
  return (CARD_NOT_PRESENT_ENTRY_MODES as readonly string[]).includes(
    entryMode ?? "",
  );
}

// Category returned by the provider-specific response classifier. This
// is the neutral, provider-agnostic characterization of a response;
// downstream code branches on this, never on the raw provider code.
export const PAYMENT_RESPONSE_CATEGORIES = [
  "Approved",
  "PartialApproval",
  "Declined",            // any issuer or scheme decline
  "AvsDeclined",         // AVS verification did not match (business-decide)
  "CvvDeclined",         // CVV did not match (business-decide)
  "Duplicate",           // provider duplicate-detection short-circuit
  "Timeout",             // transport timeout or issuer timeout
  "SystemRejected",      // processor rejected for config / rules reasons
  "InvalidInstrument",   // bad card number / bad account
  "ProviderUnavailable", // 5xx / connectivity from the provider
  "ReaderError",         // POS reader-side error (not gateway)
  "Unknown",             // an outcome the classifier could not categorize
] as const;
export type PaymentResponseCategory =
  (typeof PAYMENT_RESPONSE_CATEGORIES)[number];
